import React, { useCallback, useEffect, useState } from 'react';
import { HeartIcon } from '@heroicons/react/24/outline';
import { playersAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { getErrorMessage } from '../../utils/errorMessage';
import type { Player, UpdatePlayerContactRequest } from '../../types/player';
import PlayerContactForm from './PlayerContactForm';
import AbsencePanel from './AbsencePanel';

/**
 * Profil: a játékos maga, illetve a szülő a gyermekei elérhetőségeit és
 * egészségügyi adatait frissítheti, és itt jelentheti be a betegséget / sérülést.
 */
const MyPlayersCard: React.FC = () => {
  const { showToast } = useToast();
  const [players, setPlayers] = useState<Player[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [savingId, setSavingId] = useState<number | null>(null);

  const load = useCallback(async () => {
    try {
      const mine = await playersAPI.getMyPlayers();
      setPlayers(mine);
      setSelectedId((prev) => prev ?? mine[0]?.id ?? null);
    } catch (err) {
      console.error('A saját játékosok betöltése nem sikerült:', err);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (!loaded || players.length === 0) return null;

  const selected = players.find((p) => p.id === selectedId) ?? players[0];

  const handleSave = async (data: UpdatePlayerContactRequest) => {
    setSavingId(selected.id);
    try {
      const updated = await playersAPI.updateContact(selected.id, data);
      setPlayers((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
      showToast('Adatok elmentve.');
    } catch (err) {
      showToast(getErrorMessage(err, 'A mentés nem sikerült.'), 'error');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="overflow-hidden rounded-xl bg-white shadow-sm">
      <div className="border-b border-gray-100 card-header bg-gradient-to-r from-rose-50 to-white px-4 py-4 md:px-6">
        <div className="flex items-center space-x-3">
          <div className="card-header-icon rounded-lg bg-rose-100 p-2">
            <HeartIcon className="h-6 w-6 text-rose-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              {players.length === 1 && players[0].userId ? 'Elérhetőség és egészség' : 'Gyermekeim adatai'}
            </h2>
            <p className="text-sm text-gray-500">Elérhetőség, egészségügyi tudnivalók, betegség bejelentése.</p>
          </div>
        </div>
      </div>

      <div className="space-y-6 p-4 md:p-6">
        {players.length > 1 && (
          <div className="flex flex-wrap gap-2">
            {players.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelectedId(p.id)}
                className={`rounded-full px-3 py-1.5 text-sm font-medium ${
                  p.id === selected.id ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        )}

        <section>
          <h3 className="mb-2 text-sm font-semibold text-gray-900">Betegség, sérülés</h3>
          <AbsencePanel key={selected.id} playerId={selected.id} playerName={selected.name} />
        </section>

        <section>
          <h3 className="mb-2 text-sm font-semibold text-gray-900">Elérhetőség és egészségügyi adatok</h3>
          <PlayerContactForm player={selected} saving={savingId === selected.id} onSubmit={handleSave} />
          {selected.contactUpdatedAt && (
            <p className="mt-2 text-xs text-gray-400">
              Utoljára frissítve: {new Date(selected.contactUpdatedAt).toLocaleDateString('hu-HU')}
            </p>
          )}
        </section>
      </div>
    </div>
  );
};

export default MyPlayersCard;
